'use client';

import dayjs from 'dayjs';

import { Button, CrownSimpleIcon, DotsThreeVerticalIcon } from '@/shared';
import type { ActiveSubscriptionResponseV1 } from '@/shared/api';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

import { CreditCard } from './credit-card';

type ActiveSubscriptionItemProps = React.PropsWithChildren<{
	subscription: ActiveSubscriptionResponseV1;
	onCancel: () => void;
	cancelDisabled?: boolean;
}>;

export function ActiveSubscriptionItem({
	subscription,
	onCancel,
	cancelDisabled = false,
}: ActiveSubscriptionItemProps) {
	return (
		<div className="relative flex flex-col items-start gap-6 rounded-xl border-[#FFFFFF08] bg-[#F5F8FF] p-6 md:flex-row md:items-center">
			<div className="rounded-lg bg-white bg-gradient-to-t from-[#0149FF14] to-[#0149FF14] p-6">
				<CrownSimpleIcon className="size-12" />
			</div>
			<div className="flex-1 space-y-1">
				<div className="text-green">
					Активна до{' '}
					<span className="font-bold">
						{dayjs(subscription.expirationDate).format('DD.MM.YYYY')}
					</span>
				</div>
				<h2 className="text-xl font-bold lg:text-2xl">
					{subscription.subscriptionName}
				</h2>
				{subscription.label && (
					<div className="inline-block rounded-lg bg-accent/10 px-3 py-1">
						<span className="text-sm font-semibold text-accent">
							{subscription.label}
						</span>
					</div>
				)}
				{!subscription.recurrentEnabled ? (
					<div className="text-gray-3">Автоматические списания отключены</div>
				) : subscription.nextPaymentDate ? (
					<div className="text-gray-3">
						Следующее списание {subscription.amount}₽ -{' '}
						{dayjs(subscription.nextPaymentDate).format('DD.MM.YYYY')}
					</div>
				) : (
					<div className="text-gray-3">Автопродление включено</div>
				)}
			</div>

			<div className="flex items-center gap-2">
				{subscription?.cardInfo?.lastFourDigits && (
					<div className="w-full max-w-80 md:w-auto">
						<CreditCard card={subscription.cardInfo} />
					</div>
				)}

				{subscription.recurrentEnabled && (
					<>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="plain-gray"
									className="max-sm:absolute max-sm:right-2 max-sm:top-4"
								>
									<DotsThreeVerticalIcon className="size-8" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem variant="destructive" asChild>
									<button
										className="w-full whitespace-nowrap px-3 py-2 text-left text-red"
										type="button"
										disabled={cancelDisabled}
										onClick={onCancel}
									>
										Отменить подписку
									</button>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</>
				)}
			</div>
		</div>
	);
}
